import { z } from 'zod';

/** One ledger decision per saved-plan action; the plan only links to it (#11394). */
export const SAVED_ACTION_DECISION_TYPE = 'saved_recovery_action';
export const SAVED_ACTION_SCOPE_KIND = 'saved_plan_action';
export const ACTION_DECISIONS_PATH = 'v1/views/action-decisions';
const id = z.string().trim().min(1).max(512);
const day = z.string().date();
export const actionPlanPinSchema = z.object({ definitionVersion: z.number().int().positive(),
  definitionDigest: id, contentDigest: id }).strict();
export const actionDecisionOwnerSchema = z.object({ personRef: id, label: id }).strict();
const identity = { dealId: id, asOf: day, stepRef: id, plan: actionPlanPinSchema };
export const actionDecisionRequestSchema = z.discriminatedUnion('disposition', [
  z.object({ ...identity, disposition: z.literal('accept'), checkpointDate: day }).strict(),
  z.object({ ...identity, disposition: z.literal('modify'), ownerPersonRef: id,
    dueDate: day, checkpointDate: day }).strict(),
  z.object({ ...identity, disposition: z.literal('reject'),
    reason: z.string().trim().min(1).max(2000) }).strict(),
]);
export const ACTION_DECISION_REFUSALS = {
  INVALID_REQUEST: 'The decision details are invalid.',
  UNAUTHORIZED: 'You do not have permission to decide this action.',
  NO_ACTIVE_PLAN: 'There is no active saved plan to decide.',
  ACTION_NOT_IN_PLAN: 'This action is not in the active saved plan.',
  STALE_PLAN: 'The plan changed. Refresh before deciding.',
  OWNER_NOT_GOVERNED: 'Choose a person from the program directory.',
  DUE_DATE_REQUIRED: 'Set a due date before accepting this action.',
  CHECKPOINT_BEFORE_PLAN: 'The checkpoint date must be on or after the plan date.',
  EFFECT_NOT_AVAILABLE: 'This action needs a published expected effect before acceptance.',
  NO_ACTIVE_STRATEGY: 'An active strategy is required to record this decision.',
  DECISION_CONFLICT: 'This action has changed or already been decided. Refresh to see the decision.',
  INVALID_LEDGER_DECISION: 'The recorded decision could not be read.',
} as const;
export type ActionDecisionRefusalCode = keyof typeof ACTION_DECISION_REFUSALS;
const refusalCodes = Object.keys(ACTION_DECISION_REFUSALS) as
  [ActionDecisionRefusalCode, ...ActionDecisionRefusalCode[]];
export const actionDecisionViewSchema = z.object({ decisionId: id,
  state: z.enum(['accepted', 'rejected']), action: id, owner: z.string().nullable(),
  ownerPersonRef: id.nullable(), dueDate: day.nullable(), checkpointDate: day.nullable(),
  decidedAt: z.string().datetime(), decidedByRole: id, reason: z.string().nullable(),
  checkpointMessage: z.string().nullable(),
  /** #11468: an owner is recorded but this viewer lacks people-PII read; a null owner alone is absent. */
  ownerWithheld: z.literal(true).optional() }).strict();
/** Server-owned metadata on the ledger row; `source` is validated by the API's row schema. */
export const savedActionDecisionRecordSchema = actionDecisionViewSchema
  .omit({ decisionId: true, checkpointMessage: true, ownerWithheld: true }).extend({
    version: z.literal(1), dealId: id, stepRef: id, commitmentRefs: z.array(id).min(1),
    plan: actionPlanPinSchema, decidedByUserId: id, request: actionDecisionRequestSchema,
    source: z.record(z.string(), z.unknown()),
  }).strict();
export const actionDecisionResponseSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), decision: actionDecisionViewSchema }).strict(),
  z.object({ ok: z.literal(false), refusal: z.object({ code: z.enum(refusalCodes),
    message: z.string() }).strict() }).strict(),
]);
/** Decisions-page eligibility only; the server re-checks every condition when writing. */
export const actionDecisionContextSchema = z.object({ pin: actionPlanPinSchema,
  canDecide: z.boolean(), reason: z.string().nullable(),
  owners: z.array(actionDecisionOwnerSchema) }).strict();
export const actionDecisionRowSchema = z.object({ ownerPersonRef: id.nullable(),
  dueDate: day.nullable(), checkpointDate: day.nullable(), canAccept: z.boolean(),
  canModify: z.boolean(), reason: z.string().nullable() }).strict();
export type ActionDecisionRequest = z.infer<typeof actionDecisionRequestSchema>;
export type ActionDecisionOwner = z.infer<typeof actionDecisionOwnerSchema>;
export type ActionDecisionView = z.infer<typeof actionDecisionViewSchema>;
export type ActionDecisionResponse = z.infer<typeof actionDecisionResponseSchema>;
export type ActionPlanPin = z.infer<typeof actionPlanPinSchema>;
export type SavedActionDecisionRecord = z.infer<typeof savedActionDecisionRecordSchema>;
export type ActionDecisionContext = z.infer<typeof actionDecisionContextSchema>;
export type ActionDecisionRow = z.infer<typeof actionDecisionRowSchema>;
